import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Select,
  StatePanel,
  V2Avatar,
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  V2Modal,
  V2SearchInput,
  V2Skeleton,
  V2Table,
  V2Tabs,
  V2ToastStack,
  Icon,
  type V2BadgeTone,
  type V2TableColumn,
  type V2ToastItem,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { ApiError, apiPatch } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  NO_PROJECT_LABEL,
  TASK_STATUS_OPTIONS,
  type ProjectTask,
  type ProjectTaskListResponse,
  type TaskAssignee,
} from '../../lib/types/projects.js';
import { QuickTaskForm } from './QuickTaskForm.js';
import { TaskDrawer } from './TaskDrawer.js';
import './project-tasks.css';

type StatusValue = (typeof TASK_STATUS_OPTIONS)[number]['value'];
type ViewMode = 'board' | 'list';

const STATUS_BY_LABEL: Record<string, StatusValue> = Object.fromEntries(
  TASK_STATUS_OPTIONS.map((o) => [o.label, o.value]),
);
const LABEL_BY_STATUS: Record<StatusValue, string> = Object.fromEntries(
  TASK_STATUS_OPTIONS.map((o) => [o.value, o.label]),
) as Record<StatusValue, string>;

const COLUMNS: readonly { id: StatusValue; dot: string }[] = [
  { id: 'todo', dot: '' },
  { id: 'in_progress', dot: 'info' },
  { id: 'done', dot: 'success' },
  { id: 'cancelled', dot: 'danger' },
];

/** Forward path used by the card's move buttons; «لغو شده» is reached by drag or the list. */
const FLOW: readonly StatusValue[] = ['todo', 'in_progress', 'done'];

const TOAST_MS = 5000;

const fa = (n: number) => n.toLocaleString('fa-IR');

function statusOf(task: ProjectTask): StatusValue {
  return STATUS_BY_LABEL[task.statusLabel] ?? 'todo';
}

function isOpen(status: StatusValue): boolean {
  return status === 'todo' || status === 'in_progress';
}

function priorityTone(label: string): V2BadgeTone {
  if (label === 'فوری' || label === 'urgent') return 'danger';
  if (label === 'بالا' || label === 'high') return 'warn';
  if (label === 'متوسط' || label === 'medium') return 'info';
  return 'neutral';
}

/** Priorities worth a badge on the card; normal/low stay quiet to cut noise. */
function isLoudPriority(label: string): boolean {
  return ['فوری', 'urgent', 'بالا', 'high'].includes(label);
}

function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? '')
    .join('‌');
}

function formatSpent(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${fa(m)} دقیقه`;
  return m === 0 ? `${fa(h)} ساعت` : `${fa(h)}:${fa(m).padStart(2, '۰')} ساعت`;
}

interface DueInfo {
  readonly tone: V2BadgeTone;
  readonly text: string;
  readonly overdue: boolean;
}

/** Due chip: lateness is stated in words and icon, never by colour alone. */
function dueInfo(task: ProjectTask, today: string): DueInfo | null {
  if (!task.dueDate) return null;
  const date = displayJalaliDate(task.dueDate);
  if (!isOpen(statusOf(task))) return { tone: 'neutral', text: date, overdue: false };
  if (task.dueDate < today) return { tone: 'danger', text: `معوق · ${date}`, overdue: true };
  if (task.dueDate === today) return { tone: 'warn', text: 'امروز', overdue: false };
  return { tone: 'neutral', text: date, overdue: false };
}

function BoardSkeleton() {
  return (
    <div className="prj-tasks__board" aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((col) => (
        <div key={col} className="prj-tasks__skeleton-col">
          <V2Skeleton width={110} height={14} />
          {[0, 1].map((card) => (
            <div key={card} className="prj-tasks__skeleton-card">
              <V2Skeleton width="40%" height={10} />
              <V2Skeleton height={14} />
              <V2Skeleton width="65%" height={12} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ProjectTasksPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const today = systemClock.today();

  const res = useApiGet<ProjectTaskListResponse>(
    slug ? `${base}/api/v1/projects/tasks?limit=200` : null,
    [slug],
  );

  // Local copy of the list so a status change can show instantly (and roll back)
  // without a full refetch; it also keeps the board on screen during a reload.
  const [tasks, setTasks] = useState<readonly ProjectTask[] | null>(null);
  // Keyed on the fetched payload, not `res` (a fresh object every render), so an
  // optimistic edit is only replaced when the server really returns new data.
  const fetched = res.status === 'ready' ? res.data.tasks : null;
  useEffect(() => {
    if (fetched) setTasks(fetched);
  }, [fetched]);

  const [view, setView] = useState<ViewMode>('board');
  const [search, setSearch] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [creating, setCreating] = useState(false);

  // The open quick-view lives in the URL (?task=REF) so it survives reload and can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const openRef = searchParams.get('task');
  const openTask = useCallback(
    (e: MouseEvent<HTMLAnchorElement>, ref: string) => {
      // Let ctrl/cmd/middle-click open the full page in a new tab as usual.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('task', ref);
        return next;
      });
    },
    [setSearchParams],
  );
  const closeTask = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }, [setSearchParams]);
  const setAssignee = useCallback((ref: string, assignee: TaskAssignee | null) => {
    setTasks((prev) => (prev ? prev.map((t) => (t.ref === ref ? { ...t, assignee } : t)) : prev));
  }, []);

  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [dragRef, setDragRef] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<StatusValue | null>(null);

  const [toasts, setToasts] = useState<readonly V2ToastItem[]>([]);
  const toastSeq = useRef(0);
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const pushToast = useCallback(
    (message: V2ToastItem['message'], tone: V2ToastItem['tone'] = 'neutral') => {
      toastSeq.current += 1;
      const id = `t${toastSeq.current}`;
      setToasts((prev) => [...prev, { id, message, tone }]);
      window.setTimeout(() => dismissToast(id), TOAST_MS);
    },
    [dismissToast],
  );

  const moveTask = useCallback(
    async (task: ProjectTask, to: StatusValue, withUndo = true) => {
      const from = statusOf(task);
      if (from === to || pending.has(task.ref)) return;
      const setLabel = (label: string) =>
        setTasks((prev) =>
          prev ? prev.map((t) => (t.ref === task.ref ? { ...t, statusLabel: label } : t)) : prev,
        );
      const setBusy = (busy: boolean) =>
        setPending((prev) => {
          const next = new Set(prev);
          if (busy) next.add(task.ref);
          else next.delete(task.ref);
          return next;
        });

      setLabel(LABEL_BY_STATUS[to]);
      setBusy(true);
      try {
        await apiPatch(`${base}/api/v1/projects/tasks/${encodeURIComponent(task.ref)}`, {
          status: to,
        });
        pushToast(
          withUndo ? (
            <>
              «{task.title}» به «{LABEL_BY_STATUS[to]}» رفت.{' '}
              <V2Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  void moveTask({ ...task, statusLabel: LABEL_BY_STATUS[to] }, from, false)
                }
              >
                بازگردانی
              </V2Button>
            </>
          ) : (
            `«${task.title}» به «${LABEL_BY_STATUS[to]}» برگشت.`
          ),
          'success',
        );
      } catch (error) {
        setLabel(LABEL_BY_STATUS[from]);
        pushToast(
          error instanceof ApiError && error.status === 403
            ? 'برای تغییر وضعیت این کار دسترسی ندارید.'
            : 'تغییر وضعیت انجام نشد؛ دوباره تلاش کنید.',
          'danger',
        );
      } finally {
        setBusy(false);
      }
    },
    [base, pending, pushToast],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tasks ?? []).filter((t) => {
      if (overdueOnly && !(dueInfo(t, today)?.overdue ?? false)) return false;
      if (unassignedOnly && t.assignee !== null) return false;
      if (!q) return true;
      return [t.title, t.ref, t.projectRef ?? '', t.assignee?.displayLabel ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [tasks, search, overdueOnly, unassignedOnly, today]);

  if (!slug) return null;

  const hasFilters = search.trim() !== '' || overdueOnly || unassignedOnly;
  const total =
    res.status === 'ready' && typeof res.data.total === 'number'
      ? res.data.total
      : (tasks?.length ?? 0);

  const newTaskButton = (
    <V2Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
      کار جدید
    </V2Button>
  );

  function onDragStart(e: DragEvent<HTMLElement>, task: ProjectTask) {
    e.dataTransfer.setData('text/plain', task.ref);
    e.dataTransfer.effectAllowed = 'move';
    setDragRef(task.ref);
  }
  function onDragEnd() {
    setDragRef(null);
    setOverCol(null);
  }
  function onDrop(e: DragEvent<HTMLElement>, col: StatusValue) {
    e.preventDefault();
    const ref = e.dataTransfer.getData('text/plain') || dragRef;
    const task = tasks?.find((t) => t.ref === ref);
    onDragEnd();
    if (task) void moveTask(task, col);
  }

  function renderCard(task: ProjectTask) {
    const status = statusOf(task);
    const due = dueInfo(task, today);
    const flowIndex = FLOW.indexOf(status);
    const prev = flowIndex > 0 ? FLOW[flowIndex - 1] : undefined;
    const next = flowIndex >= 0 && flowIndex < FLOW.length - 1 ? FLOW[flowIndex + 1] : undefined;
    const checklist = task.checklistSummary;
    const spent = task.totalSpentMinutes ?? 0;

    return (
      <article
        key={task.ref}
        className={[
          'prj-tasks__card',
          due?.overdue ? 'is-overdue' : '',
          dragRef === task.ref ? 'is-dragging' : '',
          pending.has(task.ref) ? 'is-pending' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        draggable={!pending.has(task.ref)}
        onDragStart={(e) => onDragStart(e, task)}
        onDragEnd={onDragEnd}
      >
        <div className="prj-tasks__card-top">
          <span className="prj-tasks__ref">{task.ref}</span>
          {isLoudPriority(task.priorityLabel) ? (
            <V2Badge tone={priorityTone(task.priorityLabel)}>{task.priorityLabel}</V2Badge>
          ) : null}
        </div>

        <Link
          to={`${base}/projects/tasks/${encodeURIComponent(task.ref)}`}
          className="prj-tasks__card-link"
          onClick={(e) => openTask(e, task.ref)}
        >
          {task.title}
        </Link>

        <div className="prj-tasks__tags">
          <span className="prj-tasks__tag">
            <Icon name="projects" size="sm" />
            {task.projectRef ?? NO_PROJECT_LABEL}
          </span>
          {task.relatedType ? (
            <span className="prj-tasks__tag">
              <Icon name="tag" size="sm" />
              {task.relatedTypeLabel ?? task.relatedType}
              {task.relatedRef ? `: ${task.relatedRef}` : ''}
            </span>
          ) : null}
        </div>

        <div className="prj-tasks__card-foot">
          {task.assignee ? (
            <span className="prj-tasks__assignee">
              <V2Avatar initials={initialsOf(task.assignee.displayLabel)} size="sm" />
              <span className="prj-tasks__assignee-name">{task.assignee.displayLabel}</span>
            </span>
          ) : (
            <span className="prj-tasks__assignee prj-tasks__assignee--none">
              <Icon name="user" size="sm" />
              بدون مسئول
            </span>
          )}
          {due ? (
            <V2Badge tone={due.tone} plain={due.tone === 'neutral'}>
              <Icon name="calendar" size="sm" /> {due.text}
            </V2Badge>
          ) : null}
          {checklist && checklist.total > 0 ? (
            <span
              className={`prj-tasks__meta${checklist.done === checklist.total ? ' prj-tasks__meta--done' : ''}`}
              title="گام‌های چک‌لیست"
            >
              <Icon name="check" size="sm" />
              {fa(checklist.done)}/{fa(checklist.total)}
            </span>
          ) : null}
          {spent > 0 ? (
            <span className="prj-tasks__meta" title="زمان ثبت‌شده">
              <Icon name="clock" size="sm" />
              {formatSpent(spent)}
            </span>
          ) : null}
          {prev || next ? (
            <span className="prj-tasks__move">
              {prev ? (
                <V2Button
                  size="sm"
                  variant="ghost"
                  iconOnly
                  icon="chevronRight"
                  aria-label={`انتقال به «${LABEL_BY_STATUS[prev]}»`}
                  title={`انتقال به «${LABEL_BY_STATUS[prev]}»`}
                  disabled={pending.has(task.ref)}
                  onClick={() => void moveTask(task, prev)}
                />
              ) : null}
              {next ? (
                <V2Button
                  size="sm"
                  variant="ghost"
                  iconOnly
                  icon="chevronLeft"
                  aria-label={`انتقال به «${LABEL_BY_STATUS[next]}»`}
                  title={`انتقال به «${LABEL_BY_STATUS[next]}»`}
                  disabled={pending.has(task.ref)}
                  onClick={() => void moveTask(task, next)}
                />
              ) : null}
            </span>
          ) : null}
        </div>
      </article>
    );
  }

  function renderBoard() {
    const cancelledCount = visible.filter((t) => statusOf(t) === 'cancelled').length;
    return (
      <div className="prj-tasks__board">
        {COLUMNS.map((col) => {
          if (col.id === 'cancelled' && !showCancelled) {
            return (
              <button
                key={col.id}
                type="button"
                className="prj-tasks__col-toggle"
                onClick={() => setShowCancelled(true)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, col.id)}
              >
                <Icon name="chevronRight" size="sm" />
                {LABEL_BY_STATUS.cancelled} ({fa(cancelledCount)})
              </button>
            );
          }
          const colTasks = visible.filter((t) => statusOf(t) === col.id);
          return (
            <section
              key={col.id}
              className={`prj-tasks__col${overCol === col.id ? ' is-drop-target' : ''}`}
              aria-label={LABEL_BY_STATUS[col.id]}
              onDragOver={(e) => {
                e.preventDefault();
                if (overCol !== col.id) setOverCol(col.id);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOverCol(null);
              }}
              onDrop={(e) => onDrop(e, col.id)}
            >
              <header className="prj-tasks__col-head">
                <span
                  className={`prj-tasks__col-dot${col.dot ? ` prj-tasks__col-dot--${col.dot}` : ''}`}
                  aria-hidden="true"
                />
                {LABEL_BY_STATUS[col.id]}
                <span className="prj-tasks__col-count">{fa(colTasks.length)}</span>
                {col.id === 'cancelled' ? (
                  <V2Button
                    size="sm"
                    variant="ghost"
                    iconOnly
                    icon="x"
                    aria-label="جمع کردن ستون"
                    onClick={() => setShowCancelled(false)}
                  />
                ) : null}
              </header>
              {colTasks.length === 0 ? (
                <div className="prj-tasks__col-empty">
                  {hasFilters ? 'موردی با این فیلترها نیست' : 'کارت را اینجا رها کنید'}
                </div>
              ) : (
                colTasks.map(renderCard)
              )}
            </section>
          );
        })}
      </div>
    );
  }

  function renderList() {
    type Row = { id: string; task: ProjectTask };
    const columns: readonly V2TableColumn<Row>[] = [
      {
        key: 'title',
        header: 'کار',
        render: ({ task }) => (
          <div className="prj-tasks__title-cell">
            <Link
              to={`${base}/projects/tasks/${encodeURIComponent(task.ref)}`}
              className="prj-tasks__card-link"
              onClick={(e) => openTask(e, task.ref)}
            >
              {task.title}
            </Link>
            <span className="prj-tasks__ref">{task.ref}</span>
          </div>
        ),
      },
      {
        key: 'project',
        header: 'پروژه',
        render: ({ task }) => task.projectRef ?? NO_PROJECT_LABEL,
      },
      {
        key: 'assignee',
        header: 'مسئول',
        render: ({ task }) =>
          task.assignee ? (
            <span className="prj-tasks__assignee">
              <V2Avatar initials={initialsOf(task.assignee.displayLabel)} size="sm" />
              {task.assignee.displayLabel}
            </span>
          ) : (
            <span className="prj-tasks__assignee prj-tasks__assignee--none">بدون مسئول</span>
          ),
      },
      {
        key: 'due',
        header: 'سررسید',
        render: ({ task }) => {
          const due = dueInfo(task, today);
          return due ? (
            <V2Badge tone={due.tone} plain={due.tone === 'neutral'}>
              {due.text}
            </V2Badge>
          ) : (
            '—'
          );
        },
      },
      {
        key: 'priority',
        header: 'اولویت',
        render: ({ task }) => (
          <V2Badge tone={priorityTone(task.priorityLabel)} plain>
            {task.priorityLabel}
          </V2Badge>
        ),
      },
      {
        key: 'status',
        header: 'وضعیت',
        render: ({ task }) => (
          <Select
            aria-label={`وضعیت ${task.title}`}
            value={statusOf(task)}
            disabled={pending.has(task.ref)}
            onChange={(e) => void moveTask(task, e.target.value as StatusValue)}
          >
            {TASK_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        ),
      },
    ];
    return (
      <V2Card clipped>
        <V2Table columns={columns} rows={visible.map((task) => ({ id: task.ref, task }))} />
      </V2Card>
    );
  }

  let body;
  if (tasks === null && res.status === 'error') {
    body =
      res.error.status === 403 ? (
        <StatePanel variant="permission_denied" title="دسترسی ندارید" />
      ) : (
        <StatePanel variant="network_error" title="فهرست قابل نمایش نیست" />
      );
  } else if (tasks === null) {
    body = <BoardSkeleton />;
  } else if (total === 0) {
    body = (
      <V2EmptyState
        icon="tasks"
        title="هنوز کاری ثبت نشده است."
        description="اولین کار را بسازید و آن را به یک همکار یا پروژه بسپارید."
        action={newTaskButton}
      />
    );
  } else if (visible.length === 0) {
    body = (
      <V2EmptyState
        icon="search"
        title="کاری با این فیلترها پیدا نشد."
        description="عبارت جستجو یا فیلترها را تغییر دهید."
        action={
          <V2Button
            onClick={() => {
              setSearch('');
              setOverdueOnly(false);
              setUnassignedOnly(false);
            }}
          >
            پاک کردن فیلترها
          </V2Button>
        }
      />
    );
  } else {
    body = view === 'board' ? renderBoard() : renderList();
  }

  return (
    <div className="prj-tasks">
      <div className="prj-tasks__toolbar">
        <V2SearchInput
          placeholder="عنوان، کد، پروژه یا مسئول"
          value={search}
          onChange={setSearch}
          width={260}
        />
        <button
          type="button"
          className="prj-tasks__chip"
          aria-pressed={overdueOnly}
          onClick={() => setOverdueOnly((v) => !v)}
        >
          <Icon name="warn" size="sm" />
          معوق
        </button>
        <button
          type="button"
          className="prj-tasks__chip"
          aria-pressed={unassignedOnly}
          onClick={() => setUnassignedOnly((v) => !v)}
        >
          <Icon name="user" size="sm" />
          بدون مسئول
        </button>
        <div className="prj-tasks__toolbar-end">
          {tasks !== null ? (
            <span className="prj-tasks__count">
              {fa(visible.length)}
              {hasFilters ? ` از ${fa(total)}` : ''} کار
            </span>
          ) : null}
          <V2Tabs
            items={[
              { id: 'board', label: 'بورد' },
              { id: 'list', label: 'فهرست' },
            ]}
            activeId={view}
            onChange={(id) => setView(id as ViewMode)}
          />
          {newTaskButton}
        </div>
      </div>

      {body}

      <V2Modal open={creating} title="کار جدید" onClose={() => setCreating(false)} size="lg">
        <QuickTaskForm
          slug={slug}
          showPriority
          onCancel={() => setCreating(false)}
          onCreated={(task) => {
            setCreating(false);
            res.reload();
            pushToast(`کار «${task.title}» ثبت شد.`, 'success');
          }}
        />
      </V2Modal>

      {openRef && tasks?.some((t) => t.ref === openRef) ? (
        <TaskDrawer
          key={openRef}
          slug={slug}
          task={tasks.find((t) => t.ref === openRef) as ProjectTask}
          onClose={closeTask}
          onMove={(task, to) => void moveTask(task, to)}
          onAssigned={setAssignee}
        />
      ) : null}

      <V2ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
